"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PARTNER_FLOW_ATTRIBUTION_ORIGIN = exports.PARTNER_FLOW_UTM_MEDIUM = exports.PartnerFlowMutationName = exports.PartnerFrontendAnalyticsEventName = void 0;
/**
 * PartnerFrontendAnalyticsEventName — event names emitted from the partner frontends
 * (aven_partner_dashboard and aven_partner_flow) to the partner `logEvent` oRPC
 * handlers.
 *
 * Naming mirrors FrontendAnalyticsEventName in ./analytics.ts:
 *   - camelCase enum key
 *   - snake_case string value, action-first (`view_*`, `click_*`)
 *
 * Convention for the snake_case value: `{verb}_{app}_{surface}_{subject}`
 *   - verb    → view | click | submit | change | error | login | logout | route
 *   - app     → dashboard | flow
 *   - surface → page / step / component name (e.g. pipeline, pii, offer)
 *   - subject → optional disambiguator (e.g. row, cta, dialog)
 *
 * App-namespacing (`dashboard_*`, `flow_*`) keeps names unambiguous for BI when
 * queries span both partner apps. Cross-cutting infrastructure events
 * (route changes, error-boundary catches, mutation failures) are prefixed by
 * verb alone and carry the app in `eventProperties`.
 *
 * The MLO-vs-applicant session dimension is NOT encoded in the name — every event
 * is enriched with a `sessionType` property by the logger, so there is one name
 * per event regardless of session.
 *
 * The partner loggers type their first argument as PartnerFrontendAnalyticsEventName
 * so magic strings do not typecheck — every new event must be registered here first.
 */
var PartnerFrontendAnalyticsEventName;
(function (PartnerFrontendAnalyticsEventName) {
    // --- Cross-cutting (app carried in eventProperties.app) ---
    PartnerFrontendAnalyticsEventName["routeChanged"] = "route_changed";
    PartnerFrontendAnalyticsEventName["errorBoundaryCaught"] = "error_boundary_caught";
    PartnerFrontendAnalyticsEventName["mutationFailed"] = "mutation_failed";
    PartnerFrontendAnalyticsEventName["flowPhaseTransitioned"] = "flow_phase_transitioned";
    PartnerFrontendAnalyticsEventName["errorEmbedMessageUnknown"] = "error_embed_message_unknown";
    // --- Dashboard: Pipeline ---
    PartnerFrontendAnalyticsEventName["viewDashboardPipeline"] = "view_dashboard_pipeline";
    PartnerFrontendAnalyticsEventName["clickDashboardPipelineInquiryRow"] = "click_dashboard_pipeline_inquiry_row";
    PartnerFrontendAnalyticsEventName["clickDashboardPipelineContinueApplication"] = "click_dashboard_pipeline_continue_application";
    // --- Dashboard: Lead Detail ---
    PartnerFrontendAnalyticsEventName["viewDashboardLeadDetail"] = "view_dashboard_lead_detail";
    PartnerFrontendAnalyticsEventName["clickDashboardLeadDetailNextAction"] = "click_dashboard_lead_detail_next_action";
    PartnerFrontendAnalyticsEventName["clickDashboardLeadDetailCopyApplicantLink"] = "click_dashboard_lead_detail_copy_applicant_link";
    PartnerFrontendAnalyticsEventName["clickDashboardLeadDetailUploadDocument"] = "click_dashboard_lead_detail_upload_document";
    PartnerFrontendAnalyticsEventName["clickDashboardLiveView"] = "click_dashboard_live_view";
    // --- Dashboard: Contact Support ---
    PartnerFrontendAnalyticsEventName["clickDashboardSupportZoomJoin"] = "click_dashboard_support_zoom_join";
    // --- Dashboard: Live View (backend-emitted audit of the request lifecycle) ---
    PartnerFrontendAnalyticsEventName["dashboardLiveViewRequested"] = "dashboard_live_view_requested";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewAccepted"] = "dashboard_live_view_accepted";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewDeclined"] = "dashboard_live_view_declined";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewExpired"] = "dashboard_live_view_expired";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewCancelled"] = "dashboard_live_view_cancelled";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewEnded"] = "dashboard_live_view_ended";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewViewed"] = "dashboard_live_view_viewed";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewNotaryHandoffStarted"] = "dashboard_live_view_notary_handoff_started";
    PartnerFrontendAnalyticsEventName["dashboardLiveViewNotaryHandoffAttached"] = "dashboard_live_view_notary_handoff_attached";
    // --- Dashboard: Invite (public, unauthenticated) ---
    PartnerFrontendAnalyticsEventName["viewDashboardInvite"] = "view_dashboard_invite";
    PartnerFrontendAnalyticsEventName["submitDashboardInvite"] = "submit_dashboard_invite";
    // --- Flow: PII ---
    PartnerFrontendAnalyticsEventName["viewFlowPii"] = "view_flow_pii";
    PartnerFrontendAnalyticsEventName["submitFlowPii"] = "submit_flow_pii";
    PartnerFrontendAnalyticsEventName["clickFlowPiiSubmit"] = "click_flow_pii_submit";
    PartnerFrontendAnalyticsEventName["clickFlowPiiAddressCorrectionAccept"] = "click_flow_pii_address_correction_accept";
    PartnerFrontendAnalyticsEventName["clickFlowPiiEditApplicantInfo"] = "click_flow_pii_edit_applicant_info";
    // --- Flow: Applicant Summary ---
    PartnerFrontendAnalyticsEventName["viewFlowApplicantSummary"] = "view_flow_applicant_summary";
    PartnerFrontendAnalyticsEventName["submitFlowApplicantSummary"] = "submit_flow_applicant_summary";
    PartnerFrontendAnalyticsEventName["clickFlowApplicantSummarySubmit"] = "click_flow_applicant_summary_submit";
    // --- Flow: Review Borrower Info ---
    PartnerFrontendAnalyticsEventName["viewFlowReviewBorrowerInfo"] = "view_flow_review_borrower_info";
    PartnerFrontendAnalyticsEventName["submitFlowReviewBorrowerInfo"] = "submit_flow_review_borrower_info";
    PartnerFrontendAnalyticsEventName["clickFlowReviewBorrowerInfoSubmit"] = "click_flow_review_borrower_info_submit";
    // --- Flow: Offer Selection ---
    PartnerFrontendAnalyticsEventName["viewFlowOfferSelection"] = "view_flow_offer_selection";
    PartnerFrontendAnalyticsEventName["viewFlowOfferDenial"] = "view_flow_offer_denial";
    /**
     * The interactive slider was switched off and approved offers were shown read-only.
     * `reason` carries `SliderOffReason`; `sliderMathDisagreesWithServer` is always a defect
     * worth investigating, the others are expected degradations.
     */
    PartnerFrontendAnalyticsEventName["viewFlowOfferServerAuthoritative"] = "view_flow_offer_server_authoritative";
    PartnerFrontendAnalyticsEventName["clickFlowOfferSelect"] = "click_flow_offer_select";
    PartnerFrontendAnalyticsEventName["clickFlowOfferDebtSwapOpen"] = "click_flow_offer_debt_swap_open";
    PartnerFrontendAnalyticsEventName["submitFlowOfferSelection"] = "submit_flow_offer_selection";
    PartnerFrontendAnalyticsEventName["clickFlowOfferSelectionSubmit"] = "click_flow_offer_selection_submit";
    // --- Flow: Name Confirmation ---
    PartnerFrontendAnalyticsEventName["viewFlowNameConfirmation"] = "view_flow_name_confirmation";
    PartnerFrontendAnalyticsEventName["submitFlowNameConfirmation"] = "submit_flow_name_confirmation";
    PartnerFrontendAnalyticsEventName["clickFlowNameConfirmationSubmit"] = "click_flow_name_confirmation_submit";
    PartnerFrontendAnalyticsEventName["clickFlowNameConfirmationEdit"] = "click_flow_name_confirmation_edit";
    PartnerFrontendAnalyticsEventName["clickFlowNameConfirmationEscalate"] = "click_flow_name_confirmation_escalate";
    // --- Flow: Lien Attestation ---
    PartnerFrontendAnalyticsEventName["viewFlowLienAttestation"] = "view_flow_lien_attestation";
    PartnerFrontendAnalyticsEventName["submitFlowLienAttestation"] = "submit_flow_lien_attestation";
    PartnerFrontendAnalyticsEventName["clickFlowLienAttestationSubmit"] = "click_flow_lien_attestation_submit";
    PartnerFrontendAnalyticsEventName["viewFlowLienNameMatchDialog"] = "view_flow_lien_name_match_dialog";
    PartnerFrontendAnalyticsEventName["clickFlowLienNameMatchConfirm"] = "click_flow_lien_name_match_confirm";
    PartnerFrontendAnalyticsEventName["viewFlowLienPullReportError"] = "view_flow_lien_pull_report_error";
    PartnerFrontendAnalyticsEventName["clickFlowLienPullReportsRetry"] = "click_flow_lien_pull_reports_retry";
    PartnerFrontendAnalyticsEventName["clickFlowLienSendUnfreezeLink"] = "click_flow_lien_send_unfreeze_link";
    PartnerFrontendAnalyticsEventName["clickFlowLienAddOpen"] = "click_flow_lien_add_open";
    PartnerFrontendAnalyticsEventName["clickFlowLienAddSave"] = "click_flow_lien_add_save";
    PartnerFrontendAnalyticsEventName["clickFlowLienStatedValueSubmit"] = "click_flow_lien_stated_value_submit";
    // --- Flow: Texas 50(a)(6) Attestation ---
    PartnerFrontendAnalyticsEventName["viewFlowTexas50a6Attestation"] = "view_flow_texas_50a6_attestation";
    PartnerFrontendAnalyticsEventName["submitFlowTexas50a6Attestation"] = "submit_flow_texas_50a6_attestation";
    PartnerFrontendAnalyticsEventName["clickFlowTexas50a6AttestationSubmit"] = "click_flow_texas_50a6_attestation_submit";
    PartnerFrontendAnalyticsEventName["clickFlowTexas50a6AttestationBack"] = "click_flow_texas_50a6_attestation_back";
    PartnerFrontendAnalyticsEventName["viewFlowEnterPropertyValue"] = "view_flow_enter_property_value";
    PartnerFrontendAnalyticsEventName["submitFlowEnterPropertyValue"] = "submit_flow_enter_property_value";
    PartnerFrontendAnalyticsEventName["clickFlowEnterPropertyValueSubmit"] = "click_flow_enter_property_value_submit";
    /**
     * A lien was answered as a 50(a)(6) extension of credit, revealing its closing-date question.
     * The branch worth measuring: everything downstream of the hardcut hangs off how often this
     * happens, and it is invisible in the submit event once the officer changes their mind.
     */
    PartnerFrontendAnalyticsEventName["clickFlowTexas50a6AttestationLienIsExtensionOfCredit"] = "click_flow_texas_50a6_attestation_lien_is_extension_of_credit";
    // --- Flow: Broker Disclosure ---
    PartnerFrontendAnalyticsEventName["submitFlowBrokerDisclosure"] = "submit_flow_broker_disclosure";
    PartnerFrontendAnalyticsEventName["clickFlowBrokerDisclosureSubmit"] = "click_flow_broker_disclosure_submit";
    // --- Flow: Send Applicant Link ---
    PartnerFrontendAnalyticsEventName["viewFlowSendApplicantLink"] = "view_flow_send_applicant_link";
    PartnerFrontendAnalyticsEventName["submitFlowSendApplicantLink"] = "submit_flow_send_applicant_link";
    PartnerFrontendAnalyticsEventName["clickFlowSendApplicantLinkDone"] = "click_flow_send_applicant_link_done";
    // --- Flow: Inquiry Complete ---
    PartnerFrontendAnalyticsEventName["viewFlowInquiryComplete"] = "view_flow_inquiry_complete";
    // --- Flow: Handover to Origination ---
    PartnerFrontendAnalyticsEventName["viewFlowHandoverToOrigination"] = "view_flow_handover_to_origination";
    PartnerFrontendAnalyticsEventName["submitFlowHandoverToOrigination"] = "submit_flow_handover_to_origination";
    // --- Flow: MLO Eligibility (entry gate) ---
    PartnerFrontendAnalyticsEventName["viewFlowMloEligibility"] = "view_flow_mlo_eligibility";
})(PartnerFrontendAnalyticsEventName || (exports.PartnerFrontendAnalyticsEventName = PartnerFrontendAnalyticsEventName = {}));
/**
 * PartnerFlowMutationName — stable identifier for each flow-side mutation that
 * wants failure telemetry via `useFlowMutation({ telemetryName })`. The value
 * lands in `flow_mutation_failed.eventProperties.telemetryName`, so BI can
 * distinguish which mutation failed without parsing the error message.
 *
 * Keep naming aligned with the feature area: `flow_{surface}_{action}`.
 * Adding a new mutation that wants failure telemetry? Register here first —
 * `useFlowMutation` only accepts values from this enum.
 */
var PartnerFlowMutationName;
(function (PartnerFlowMutationName) {
    PartnerFlowMutationName["flowPhaseTransition"] = "flow_phase_transition";
    PartnerFlowMutationName["flowOfferSaveSelection"] = "flow_offer_save_selection";
    PartnerFlowMutationName["flowOfferRerunPrequalWithDebtSwap"] = "flow_offer_rerun_prequal_with_debt_swap";
    PartnerFlowMutationName["flowLienUpdateApplicant"] = "flow_lien_update_applicant";
    PartnerFlowMutationName["flowLienSendUnfreezeLink"] = "flow_lien_send_unfreeze_link";
})(PartnerFlowMutationName || (exports.PartnerFlowMutationName = PartnerFlowMutationName = {}));
/** @TranslateToPython */
exports.PARTNER_FLOW_UTM_MEDIUM = 'partners';
/** @TranslateToPython */
exports.PARTNER_FLOW_ATTRIBUTION_ORIGIN = 'Partnership';
